<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { onKeyStroke } from '@vueuse/core';
import { useAppDownloadBonusStore } from '@/stores/app-download-bonus';
import { APP_DOWNLOAD_LINKS } from '@/utils/apostatudo/app-download-deep-link';
import { getAssetsUrlForCDN } from '@/utils/template';

// Modal "Baixe o APP e resgate o seu bônus" (Figma node 24137:59683). É o
// convite do bônus resgatável só pelo app (ENG-799): fora do app o card e os
// modais de resgate abrem ESTE cartaz, e não o genérico de
// `components/app-download/Modal.vue` (ENG-495), que continua servindo o menu
// lateral e o deep-link `?open=modal-aplicativo`.
//
// A casca é a mesma dos outros bottom sheets (Teleport pro body, slide-up por
// @keyframes porque o reset global de transition em main.scss mata
// `transition`, store controlando a abertura sem navegar de rota) — copiada de
// propósito em vez de extraída: o cartaz de lá posiciona tudo em absoluto
// contra a arte da mão, e mexer nele pra acomodar esta composição mudaria uma
// tela que já está em produção.
//
// TODAS as medidas saem do node em `cqw` contra a LARGURA da folha (393px no
// Figma → 1cqw = 3,93px), então a composição inteira encolhe junto num celular
// mais estreito. `container-type` fica na folha e as unidades nos FILHOS — cqw
// no próprio container cai na viewport.
const store = useAppDownloadBonusStore();
const { isOpen } = storeToRefs(store);

// `visible` mantém o overlay montado até o slide-down terminar; `sheetOpen`
// dispara a animação.
const visible = ref(false);
const sheetOpen = ref(false);

useBodyScrollLock(visible);

// Arte do Figma (`magnific_melhore-toda-iluminacao`, 426 × 529 na folha) em 2x.
// É o bitmap original do preenchimento, não um export de node: export de node
// vem sobre o fundo do canvas, opaco, e taparia o brilho e a faixa. A caixa
// abaixo foi conferida contra o render do node — 0,7 de erro médio numa escala
// de 0 a 255.
// Entra duas vezes: nítida e, por cima, uma cópia borrada em `lighten` a 40% —
// é o que dá o brilho nas quinas do laço e das notas.
const artImage = getAssetsUrlForCDN(
  '/assets/images/app-download/bonus-art.webp',
);

// Feixe de luz (`image 12`): no node é um quadrado de 393 girado -90° em
// `color-dodge`; a rotação já vem assada no arquivo, então aqui é só posicionar.
const streakImage = getAssetsUrlForCDN(
  '/assets/images/app-download/bonus-light-streak.webp',
);

const logoImage = getAssetsUrlForCDN(
  '/assets/images/app-download/logo-horizontal-white.webp',
);

const googlePlayIcon = getAssetsUrlForCDN(
  '/assets/images/app-download/google-play.svg',
);

const appStoreIcon = getAssetsUrlForCDN(
  '/assets/images/app-download/app-store.svg',
);

// Textos fixos (só pt-BR), pelo mesmo motivo do cartaz genérico: é peça de
// marketing com números da ficha da loja, não copy traduzível.
//
// O título vem quebrado nas duas linhas do Figma ("...resgate" / "o seu
// bônus."): a caixa dele tem a largura inteira da folha e, deixado no
// automático, um milímetro de diferença na fonte já joga "resgate" pra outra
// linha. `title` continua inteiro pro rótulo acessível.
const TEXTS = {
  title: 'Baixe o APP e resgate o seu bônus.',
  titleLines: ['Baixe o APP e resgate', 'o seu bônus.'],
  close: 'Fechar',
  rating: '4,7',
  ratingLabel: '7,58 mil avaliações',
  ageLabel: 'Classificação 18 anos',
  downloads: '500 mil+',
  downloadsLabel: 'Downloads',
} as const;

// `iconWidth`/`iconHeight` são as caixas dos dois Unions do node (18,313 ×
// 21,129 e 18,944 × 22,287): glifos diferentes, proporções diferentes, então
// cada um leva a sua — uma medida só pros dois esticaria um deles.
const stores = [
  {
    key: 'googlePlay',
    href: APP_DOWNLOAD_LINKS.bonusModal.googlePlay,
    icon: googlePlayIcon,
    iconWidth: '4.66cqw',
    iconHeight: '5.38cqw',
    prefix: 'Baixar no',
    name: 'Google Play',
  },
  {
    key: 'appStore',
    href: APP_DOWNLOAD_LINKS.bonusModal.appStore,
    icon: appStoreIcon,
    iconWidth: '4.82cqw',
    iconHeight: '5.67cqw',
    prefix: 'Baixar na',
    name: 'App Store',
  },
] as const;

function close() {
  store.close();
}

function onClosed() {
  visible.value = false;
}

watch(
  isOpen,
  (open) => {
    if (open) {
      visible.value = true;
      nextTick(() => {
        sheetOpen.value = true;
      });
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

onKeyStroke('Escape', () => {
  if (isOpen.value) close();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="adb-overlay"
      @dragstart.prevent
    >
      <div
        class="adb-backdrop"
        :class="{ 'adb-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        appear
        name="adb-slide-up"
        @after-leave="onClosed"
      >
        <div
          v-if="sheetOpen"
          :aria-label="TEXTS.title"
          aria-modal="true"
          class="adb-sheet"
          role="dialog"
        >
          <!-- Camadas de fundo, na ordem do node: brilho roxo borrado (LIGHT),
               os dois círculos (Ellipse 82/81), o feixe de luz, a faixa branca
               e a escura por cima dela, a arte, a cópia borrada da arte e o
               degradê que apaga tudo até o slate-950. Nenhuma recebe clique. -->
          <div
            aria-hidden="true"
            class="adb-glow"
          />
          <div
            aria-hidden="true"
            class="adb-shade"
          />
          <div
            aria-hidden="true"
            class="adb-violet"
          />

          <img
            alt=""
            aria-hidden="true"
            class="adb-streak"
            draggable="false"
            :src="streakImage"
          />

          <div
            aria-hidden="true"
            class="adb-band adb-band--light"
          />
          <div
            aria-hidden="true"
            class="adb-band adb-band--dark"
          />

          <img
            alt=""
            aria-hidden="true"
            class="adb-art"
            draggable="false"
            :src="artImage"
          />
          <img
            alt=""
            aria-hidden="true"
            class="adb-art adb-art--halo"
            draggable="false"
            :src="artImage"
          />

          <div
            aria-hidden="true"
            class="adb-veil"
          />

          <img
            :alt="appName()"
            class="adb-logo"
            draggable="false"
            :src="logoImage"
          />

          <button
            :aria-label="TEXTS.close"
            class="adb-close"
            type="button"
            @click="close"
          >
            <NuxtIcon name="close" />
          </button>

          <h2 class="adb-title">
            <span
              v-for="line in TEXTS.titleLines"
              :key="line"
              class="adb-title__line"
            >
              {{ line }}
            </span>
          </h2>

          <div class="adb-stores">
            <a
              v-for="item in stores"
              :key="item.key"
              class="adb-store"
              :href="item.href"
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt=""
                class="adb-store__icon"
                draggable="false"
                :src="item.icon"
                :style="{ width: item.iconWidth, height: item.iconHeight }"
              />
              <span class="adb-store__text">
                <small>{{ item.prefix }}</small>
                <strong>{{ item.name }}</strong>
              </span>
            </a>
          </div>

          <!-- Dados da ficha da loja, como no Figma (estáticos). -->
          <footer class="adb-meta">
            <div class="adb-meta__item adb-meta__item--rating">
              <span class="adb-meta__value adb-meta__value--rating">
                {{ TEXTS.rating }}
                <NuxtIcon
                  class="adb-meta__star"
                  name="star"
                />
              </span>
              <span class="adb-meta__label">
                {{ TEXTS.ratingLabel }}
              </span>
            </div>

            <span class="adb-meta__divider" />

            <div class="adb-meta__item adb-meta__item--age">
              <!-- Selo "18" em CSS (no Figma é o PNG da ficha do Google Play,
                   que borra em tela 2x) — mesma solução do cartaz genérico. -->
              <span
                aria-hidden="true"
                class="adb-meta__age"
              >
                18
              </span>
              <span class="adb-meta__label">
                {{ TEXTS.ageLabel }}
              </span>
            </div>

            <span class="adb-meta__divider" />

            <div class="adb-meta__item adb-meta__item--downloads">
              <span class="adb-meta__value">
                {{ TEXTS.downloads }}
              </span>
              <span class="adb-meta__label">
                {{ TEXTS.downloadsLabel }}
              </span>
            </div>
          </footer>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style>
.adb-slide-up-enter-active {
  animation: adb-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.adb-slide-up-leave-active {
  animation: adb-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes adb-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Mesma faixa de z-index do cartaz genérico: os dois nunca aparecem juntos, e
// ele precisa ficar acima do modal de resgate (z-100), que é quem o abre.
.adb-overlay {
  @apply fixed inset-0 z-[9998] flex flex-col justify-end select-none;

  // Desktop: cartão no centro da tela (como no Figma), não colado no rodapé.
  @media (min-width: 1024px) {
    justify-content: center;
  }

  img {
    -webkit-user-drag: none;
  }
}

.adb-backdrop {
  @apply fixed inset-0;

  background: rgba(0, 0, 0, 0.72);
  animation: adb-fade-in 0.25s ease forwards;
}

.adb-backdrop--out {
  animation: adb-fade-in 0.24s ease reverse forwards;
}

@keyframes adb-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// A folha: 393 × 594 no node. Igual ao cartaz genérico, o conteúdo é
// POSICIONADO (absoluto) contra a arte, não empilhado em fluxo — as coordenadas
// são as do Figma divididas por 3,93.
// `overflow: clip`, não `hidden`: hidden é scroll container e o foco no botão
// de fechar poderia rolar a folha.
.adb-sheet {
  position: relative;
  z-index: 10;
  // Celular: cola nas laterais até 460px; só a partir daí trava no teto de 393.
  width: 100vw;

  @media (min-width: 460px) {
    width: 393px;
  }
  aspect-ratio: 393 / 594;
  max-height: 100%;
  margin: 0 auto;
  overflow: clip;
  border-radius: 16px 16px 0 0;
  // Rectangle 566 do node, atrás de tudo.
  background: #000717;

  @media (min-width: 1024px) {
    border-radius: 16px;
  }
  box-shadow: 0 0 82px rgba(4, 58, 145, 0.9);
  container-type: inline-size;
}

// Camada LIGHT: elipse #6A00FF de 196,5 × 199,05 centrada em (196,5; 299) com
// blur gaussiano de 68,7 (stdDeviation do SVG = raio do `blur()` no CSS).
.adb-glow {
  position: absolute;
  top: 25.43cqw;
  left: 0;
  width: 100cqw;
  height: 101.3cqw;
  border-radius: 50%;
  background: #6a00ff;
  filter: blur(17.48cqw);
  pointer-events: none;
}

// Ellipse 82: preto a 28% — é o que "senta" a arte no fundo.
.adb-shade {
  position: absolute;
  top: 46.3cqw;
  left: -4.28cqw;
  width: 109.83cqw;
  height: 109.83cqw;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.28);
  pointer-events: none;
}

// Ellipse 81: roxo cheio. Some quase todo sob o degradê e a faixa escura, mas é
// ele que tinge a base do brilho.
.adb-violet {
  position: absolute;
  top: 59.36cqw;
  left: 6.43cqw;
  width: 88.41cqw;
  height: 88.41cqw;
  border-radius: 50%;
  background: #4d00e8;
  pointer-events: none;
}

.adb-streak {
  position: absolute;
  // O quadrado nasce em y=-10,39: no node ele é um recorte de 393 dentro de uma
  // caixa de 661,5 que gira -90°, e a rotação leva o eixo X do recorte pro Y da
  // folha.
  top: -2.64cqw;
  left: 0;
  width: 100cqw;
  height: 100cqw;
  opacity: 0.64;
  mix-blend-mode: color-dodge;
  filter: blur(0.94cqw);
  pointer-events: none;
}

// Vector 2 (branca) e Vector 1 (#020617, por cima) — o par que desenha a faixa
// diagonal atrás do maço de notas: as duas têm a MESMA silhueta e a escura
// entra deslocada, então o que sobra da branca é a fita fina. Recorte por
// `clip-path` em porcentagem = os mesmos vértices do path, sem mais um arquivo.
.adb-band {
  position: absolute;
  clip-path: polygon(54.31% 27.93%, 0 100%, 100% 100%, 100% 0, 54.31% 59.25%);
  transform: rotate(5.25deg);
  pointer-events: none;
}

.adb-band--light {
  top: 22.92cqw;
  left: -193.74cqw;
  width: 386.44cqw;
  height: 117.24cqw;
  background: linear-gradient(145.36deg, #fff 66.82%, #e6e6e6 100%);
}

.adb-band--dark {
  top: 32.6cqw;
  left: -201.29cqw;
  width: 382.61cqw;
  height: 116.08cqw;
  background: #020617;
}

.adb-art {
  position: absolute;
  // `max-width: none` porque a arte é MAIS LARGA que a folha (426 contra 393) e
  // o reset global de img a 100% a esmagava na horizontal.
  max-width: none;
  top: -1.02cqw;
  left: -8.4cqw;
  width: 108.4cqw;
  height: 134.61cqw;
  pointer-events: none;
}

// A segunda cópia da arte: borrada, em `lighten` a 40% (`magnific ... 3`).
.adb-art--halo {
  opacity: 0.4;
  mix-blend-mode: lighten;
  filter: blur(1.36cqw);
}

// Rectangle do node: transparente até 21,9% e slate-950 cheio a partir de
// 59,2%, começando em y=171,24. É o que separa a arte do texto.
.adb-veil {
  position: absolute;
  top: 43.57cqw;
  left: 0;
  width: 100cqw;
  height: 107.57cqw;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 21.906%,
    #020617 59.155%
  );
  pointer-events: none;
}

.adb-logo {
  position: absolute;
  top: 4.51cqw;
  left: 50%;
  display: block;
  width: 35.7cqw;
  height: auto;
  transform: translateX(-50%);
}

// Close do node: círculo de 30 com branco a 5% e o X de 10 dentro.
.adb-close {
  @apply absolute flex items-center justify-center rounded-full text-white;

  top: 2.93cqw;
  right: 2.8cqw;
  width: 7.63cqw;
  height: 7.63cqw;
  font-size: 2.54cqw;
  background: rgba(255, 255, 255, 0.05);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
}

// Título: 32,03/40,4, SemiBold, -0,32px de entreletras, caixa de largura cheia
// centrada em y=423,91 (por isso o translate, e não um `top` da primeira linha:
// a altura da caixa no Figma é menor que as duas linhas somadas).
.adb-title {
  @apply absolute flex flex-col text-center text-white;

  top: 107.87cqw;
  right: 0;
  left: 0;
  margin: 0;
  font-family: theme('fontFamily.sans');
  font-size: 8.15cqw;
  font-weight: 600;
  line-height: 1.261;
  letter-spacing: -0.01em;
  transform: translateY(-50%);
}

// As duas pílulas (124,554 × 34,36), numa linha de 257 → 7,9 de vão.
.adb-stores {
  position: absolute;
  top: 122.35cqw;
  left: 50%;
  display: flex;
  justify-content: space-between;
  width: 65.39cqw;
  transform: translateX(-50%);
}

.adb-store {
  @apply flex items-center justify-center text-white no-underline;

  gap: 1.13cqw;
  width: 31.69cqw;
  height: 8.74cqw;
  // O conteúdo não fica no centro geométrico: o Figma dá 16 de folga à esquerda
  // e 7,13 à direita, e é isso que encosta o glifo na borda.
  padding-right: 1.81cqw;
  padding-left: 4.07cqw;
  border: 1px solid rgba(100, 100, 247, 0.42);
  border-radius: 999px;
  // Gradiente radial do node (ATNB - BLUE MAIN): o foco claro fica acima da
  // borda de cima, à esquerda do meio, e escurece até #000098 na diagonal.
  // Hex literais pelo mesmo motivo do fundo da folha: o modal vai pro body por
  // Teleport, fora do escopo dos tokens `--v2-*`.
  background: radial-gradient(
    ellipse 44.15cqw 13.4cqw at 11.65cqw -1.46cqw,
    #1515e5 0%,
    #0b0bbf 50%,
    #000098 100%
  );

  &:hover {
    filter: brightness(1.12);
  }

  &__icon {
    display: block;
    flex: none;
  }

  &__text {
    display: flex;
    flex-direction: column;

    small {
      margin-bottom: -0.64cqw;
      font-size: 1.93cqw;
      font-weight: 400;
      line-height: 2.57cqw;
    }

    strong {
      font-size: 2.57cqw;
      font-weight: 700;
      line-height: 3.86cqw;
    }
  }
}

// Rodapé da ficha: linha de 343 em y=549, três colunas com as larguras do node
// separadas por dois fios.
.adb-meta {
  position: absolute;
  top: 139.69cqw;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 87.28cqw;
  transform: translateX(-50%);

  &__item {
    display: flex;
    flex: none;
    flex-direction: column;
    gap: 1.02cqw;
    align-items: center;
    text-align: center;
  }

  // No node esta coluna não tem vão: as duas linhas já vêm com altura fixa.
  &__item--rating {
    gap: 0;
    width: 23.38cqw;
  }

  &__item--age {
    width: 30.28cqw;
  }

  &__item--downloads {
    width: 18.32cqw;
  }

  &__value {
    display: inline-flex;
    gap: 0.5cqw;
    align-items: center;
    font-size: 2.66cqw;
    font-weight: 500;
    line-height: 1.428;
    color: #fff;
    letter-spacing: 0.018em;
    white-space: nowrap;

    &--rating {
      color: #fef08a;
    }
  }

  &__star {
    font-size: 2.28cqw;
    line-height: 1;
  }

  &__label {
    font-size: 2.28cqw;
    font-weight: 400;
    line-height: 1.333;
    color: #cbd5e1;
    letter-spacing: 0.025em;
    white-space: nowrap;
  }

  // Caixa do selo da ficha (15,3 × 15,56 no node).
  &__age {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.89cqw;
    height: 3.96cqw;
    font-size: 2.2cqw;
    font-weight: 700;
    line-height: 1;
    color: #fff;
    border: 1px solid #fff;
    border-radius: 0.6cqw;
    background: #000;
  }

  &__divider {
    flex: none;
    width: 1px;
    height: 4.56cqw;
    background: #e8eaed;
  }
}
</style>
